import React from 'react';
import WorkingScheduleForm from './WorkingScheduleForm';
import { TeacherWorkScheduleDTO } from '../../../types/workingSchedule';
import { getMySchedule, updateMySchedule } from '../../../services/workingScheduleApi';

export type TeacherWorkingScheduleCardProps = {
  scheduleValue?: TeacherWorkScheduleDTO;
  onScheduleChange?: (v: TeacherWorkScheduleDTO) => void;
  readOnly?: boolean;
  showSaveButton?: boolean;
  noForm?: boolean;
  // Props for maxLessonsPerStudent
  maxLessonsPerStudent?: number;
  onMaxLessonsChange?: (val: number) => void;
};

const TeacherWorkingScheduleCard: React.FC<TeacherWorkingScheduleCardProps> = ({ 
  scheduleValue, 
  onScheduleChange,
  readOnly = false,
  showSaveButton = true,
  noForm = false,
  maxLessonsPerStudent,
  onMaxLessonsChange,
}) => {
  const [value, setValue] = React.useState<TeacherWorkScheduleDTO>(scheduleValue || {
    teacherId: null,
    workingDaysMask: 0,
    startTime: '08:00',
    endTime: '18:00',
    timezone: undefined,
  });
  const [loading, setLoading] = React.useState<boolean>(!scheduleValue);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [savedMsg, setSavedMsg] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (scheduleValue) return;
    setLoading(true); setError(null); setSavedMsg(null);
    try {
      const dto = await getMySchedule();
      const next = {
        teacherId: dto.teacherId ?? null,
        workingDaysMask: dto.workingDaysMask ?? 0,
        startTime: dto.startTime ?? '08:00',
        endTime: dto.endTime ?? '18:00',
        timezone: dto.timezone ?? undefined,
      };
      setValue(next);
      onScheduleChange && onScheduleChange(next);
    } catch (e: any) {
      setError(e?.message || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [scheduleValue, onScheduleChange]);

  React.useEffect(()=>{ load(); }, [load]);

  React.useEffect(() => {
    if (scheduleValue) {
      setValue(scheduleValue);
    }
  }, [scheduleValue]);

  const handleInternalChange = (v: TeacherWorkScheduleDTO) => {
    setValue(v);
    onScheduleChange && onScheduleChange(v);
  };

  const handleSubmit = async (v: TeacherWorkScheduleDTO) => {
    if (readOnly) return;
    setSaving(true); setError(null); setSavedMsg(null);
    try {
      const dto = await updateMySchedule(v);
      setValue(dto);
      onScheduleChange && onScheduleChange(dto);
      setSavedMsg('Saved');
    } catch (e: any) {
      const msg = e?.message || (e?.error ?? null) || 'Failed to save';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: '1rem' }}>
      {loading ? (
        <div style={{ padding: '0.5rem', color: '#555', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
          <div style={{ width: 14, height: 16, border: '2px solid rgba(0,0,0,0.08)', borderTopColor: '#667eea', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          Loading schedule...
        </div>
      ) : (
        <>
          {savedMsg && (
            <div style={{ background: 'linear-gradient(135deg,#34d399,#10b981)', color: '#fff', padding: '.4rem .6rem', borderRadius: 8, fontWeight: 800, marginBottom: 8, fontSize: '0.85rem' }}>✓ {savedMsg}</div>
          )}
          <WorkingScheduleForm
            value={value}
            onSubmit={handleSubmit}
            onChange={handleInternalChange}
            loading={saving}
            error={error}
            readOnly={readOnly}
            showSaveButton={showSaveButton}
            noForm={noForm}
            maxLessonsPerStudent={maxLessonsPerStudent}
            onMaxLessonsChange={onMaxLessonsChange}
          />
        </>
      )}
    </div>
  );
};

export default TeacherWorkingScheduleCard;
